import React from 'react';
import { useThemeColors, useThemeFonts } from '@brandclub/bc-widgets';
import type { ThemeFontValue } from '@brandclub/bc-widgets';
import { resolveProductImageUrls } from '../../utils/assetUtils';
import { SectionBasedProduct } from '../types';
import { PageContext } from '../types/widget';

/** Same resolution the chat widgets use — the @font-face itself is registered by them. */
function resolveFontFamilyCss(
  fontKey: string,
  themeFonts: Record<string, ThemeFontValue>,
): string | undefined {
  const fontData = themeFonts[fontKey];
  if (!fontData) return undefined;
  return fontData.format === 'system' ? fontData.url : `"${fontData.name}", sans-serif`;
}

interface PinnedProductContextProps {
  /** Page context with product SKU */
  pageContext: PageContext;
  /** Optional product metadata (if already loaded) */
  product?: SectionBasedProduct | null;
  /** Whether the product is currently being fetched */
  isLoading?: boolean;
}

const SHOW_PINNED_PRODUCT_RATING = false;

/** Format review count for compact display (e.g. 5400 -> "5.4K") */
const formatReviewCount = (count: number): string => {
  if (count >= 1000) {
    const k = count / 1000;
    // Show one decimal if not a whole number
    return k % 1 === 0 ? `${k}K` : `${k.toFixed(1)}K`;
  }
  return count.toLocaleString();
};

/** Small filled star icon */
const StarIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="#FFBD32"
    style={{ flexShrink: 0 }}
  >
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

/**
 * A compact pinned bar showing the current product context at the top of the chat.
 * Displays when the chat is scoped to a specific product (on PDP page).
 */
export const PinnedProductContext: React.FC<PinnedProductContextProps> = ({
  pageContext,
  product,
  isLoading = false,
}) => {
  const { themeColors } = useThemeColors();
  const { themeFonts } = useThemeFonts();
  // Body text — NOT the brand accent; the accent made the whole bar render red.
  const primaryColor = themeColors.textPrimary || '#1F2937';
  const fontFamilyCss = resolveFontFamilyCss('studio-brand-font', themeFonts);

  // Only show on PDP pages with a stacklineSku
  if (pageContext?.pageType !== 'pdp' || !pageContext?.stacklineSku) {
    return null;
  }

  const stacklineSku = pageContext.stacklineSku;

  const { src: imageUrl } = resolveProductImageUrls(product ?? { stacklineSku });

  // Get product title and brand from metadata
  const productTitle = product?.title;
  const rating = product?.reviewsRating ?? product?.reviewsAverageRating;
  const reviewCount = product?.reviewsCount;

  // Loading skeleton
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '8px 16px',
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #f3f3f3',
          borderRadius: '10px 10px 0 0',
          boxShadow: '8px 8px 14px 0px rgba(0, 0, 0, 0.03)',
        }}
      >
        {/* Skeleton image */}
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '6px',
            backgroundColor: '#f3f4f6',
            flexShrink: 0,
          }}
        />
        {/* Skeleton text */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
          }}
        >
          <div
            style={{
              height: '14px',
              width: '70%',
              borderRadius: '4px',
              backgroundColor: '#f3f4f6',
            }}
          />
          <div
            style={{
              height: '12px',
              width: '40%',
              borderRadius: '4px',
              backgroundColor: '#f3f4f6',
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '8px 16px',
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #f3f3f3',
        borderRadius: '10px 10px 0 0',
        boxShadow: '8px 8px 14px 0px rgba(0, 0, 0, 0.03)',
        fontFamily: fontFamilyCss,
      }}
    >
      {/* Product Image */}
      <div
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '6px',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img
          src={imageUrl}
          alt={productTitle || 'Product'}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>

      {/* Product Info */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
        }}
      >
        {productTitle ? (
          <span
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: primaryColor,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: '24px',
            }}
            title={productTitle}
          >
            {productTitle}
          </span>
        ) : (
          <span
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: primaryColor,
              lineHeight: '24px',
            }}
          >
            This product
          </span>
        )}
        {SHOW_PINNED_PRODUCT_RATING && rating != null && rating > 0 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <StarIcon />
            <span
              style={{
                fontSize: '12px',
                fontWeight: 500,
                color: primaryColor,
                lineHeight: '16px',
              }}
            >
              {rating.toFixed(1)}
              {reviewCount != null && reviewCount > 0 && (
                <span style={{ color: primaryColor }}>
                  {' '}({formatReviewCount(reviewCount)} reviews)
                </span>
              )}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
